import { BaseService } from '@common/http-layer/service/base.service'
import type { AmountSummaryRequest, AmountSummaryResponse, CountryStateInfo, TaxAutomaticConfig } from '../types/payment.types'

/**
 * Tax-related endpoints. Two of them live under the REST API root and one
 * under the communities service:
 *
 *  - POST /products/amount-summary                                          (REST_API_URLS)
 *  - GET  /store/country/state                                              (REST_API_URLS)
 *  - GET  /locations/:locationId/tax/automatic-config                       (COMMUNITIES_SERVER_URL)
 *
 * `this.config.baseURL` is instance state — every method sets it explicitly
 * so call order on a shared instance can't leak the wrong host into the next
 * call (matches the pattern in `currentUser.service.ts`).
 */
export class TaxService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
    })
  }

  /**
   * POST /products/amount-summary
   * Returns subtotal, total, and per-tax breakdown given a (locationId, products, address) tuple.
   * Address fields are optional — without them the response represents the pre-tax base amount.
   *
   * Per repo convention: services do not handle errors. The caller (composable / query store)
   * is responsible for normalization.
   */
  async getAmountSummary(payload: AmountSummaryRequest): Promise<AmountSummaryResponse> {
    this.config.baseURL = 'REST_API_URLS'
    const response = await this.post<AmountSummaryResponse>('/products/amount-summary', payload)
    return response.data
  }

  /**
   * GET /store/country/state
   * Returns the full country list with embedded state arrays for the buyer-address dropdowns.
   * Static for the lifetime of a session — caller should cache aggressively (5-min staleTime).
   */
  async getCountryState(): Promise<{ data: CountryStateInfo[] }> {
    this.config.baseURL = 'REST_API_URLS'
    const response = await this.get<{ data: CountryStateInfo[] }>('/store/country/state')
    return response.data
  }

  /**
   * GET /locations/:locationId/tax/automatic-config (on COMMUNITIES_SERVER_URL)
   * Returns location-level automatic taxation configuration.
   *
   * Wire format: `{ enabled: boolean, avalara?: { companyId?: number } }`.
   * We normalize `enabled` → `automaticTaxesEnabled` so callers stay
   * decoupled from the upstream key naming.
   */
  async getTaxAutomaticConfig(locationId: string): Promise<TaxAutomaticConfig> {
    this.config.baseURL = 'COMMUNITIES_SERVER_URL'
    const { data } = await this.get<{ enabled?: boolean; avalara?: { companyId?: number } }>(
      `/locations/${encodeURIComponent(locationId)}/tax/automatic-config`
    )
    return {
      automaticTaxesEnabled: Boolean(data?.enabled),
      avalara: data?.avalara,
    }
  }
}
